import { NotificationType } from '@components/Notification/Notification';
import { InboundProcess, OutboundProcess } from '@features/supplyChain/types';
import { BillingCycle } from '@views/Invoice/invoiceTypes';
import { Match } from '@views/WarehouseDetails/WarehouseDetails';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { NotificationPreferences } from '../services/types';

// General
export const versionAtom = atom<string>('0.0.0');
export const backendVersionAtom = atom<string>('0.0.0');
export const xHeaderVersionAtom = atom<string>('0.0.0');
export const errorResponseStatusCodeAtom = atom<number | null>(null);
export const responseErrorMessagesAtom = atom<string[]>([]);
export const confirmationMessageAtom = atom<string>('');
export const isPublicPageAtom = atom<boolean>(false);

// Noticeboard
export const isInboundSidebarOpenAtom = atom<boolean>(false);
export const isOutboundSidebarOpenAtom = atom<boolean>(false);
export const isInvoiceSidebarOpenAtom = atom<boolean>(false);
export const selectedInboundProcessIdAtom = atom<string>('');
export const selectedOutboundProcessIdAtom = atom<string>('');
export const selectedInvoiceIdAtom = atom<string>('');
export const billingCyclesAtom = atom<BillingCycle[]>([]);
export const paidBillingCyclesAtom = atom<BillingCycle[]>([]);
export const inboundProcessesAtom = atom<InboundProcess[]>([]);
export const outboundProcessesAtom = atom<OutboundProcess[]>([]);
export const selectedWarehouseIdAtom = atom<string>('');
export const selectedShipperIdAtom = atom<string>('');

// Warehouse
export const isWarehouseModalOpenAtom = atom<boolean>(false);

// Fleet
export const isUploadFleetModalOpenAtom = atom<boolean>(false);

// Pricing List
export const isRatecardModalOpenAtom = atom<boolean>(false);
export const isRatecardEditModalOpenAtom = atom<boolean>(false);
export const ratecardPricingTypeAtom = atom<string>('');

// Shipments
export const selectedShipmentNoteNumberAtom = atom<string>('');

// Stock
export const stockRefreshAtom = atom<number>(0);

// Product
export const isUploadSKUsModalOpenAtom = atom<boolean>(false);

// Fleet availability
export const isPartialAvailabilityModalOpenAtom = atom<boolean>(false);
export const partialAvaiabilityVehicleIdAtom = atom<string>('');
export const fullCapacityVehiclesTomorrowAtom = atom<string[]>([]);
export const notAvailableVehiclesTomorrowAtom = atom<string[]>([]);
export const fullCapacityVehiclesDayAfterTomorrowAtom = atom<string[]>([]);
export const notAvailableVehiclesDayAfterTomorrowAtom = atom<string[]>([]);
export const tomorrowOrDayAfterAtom = atom<string>('tomorrow');
export const createdVehicleAvailabilityIdsAtom = atom<
  { vehicleId: string; date: string }[]
>([]);
export const vehicleAvailabilityIdsAtom = atom<
  { availabilityId: string; vehicleId: string; date: string }[]
>([]);

// Customer Master
export const isCustomerModalOpenAtom = atom<boolean>(false);

// Customer Master
export const isSupplierModalOpenAtom = atom<boolean>(false);

// Matching
export const matchAtom = atom<Match | null>(null);

// Business Profile
export const businessProfileAtom = atom<Record<string, any> | null>(null);

// Notifications
export const notificationsAtom = atom<NotificationType[]>([]);
export const showNotificationAtom = atom<boolean>(false);
export const unreadNotificationsCountAtom = atom<number>(0);
export const inboxCategoryFilterAtom = atom<string>('all');
// Server-persisted notification preferences. `null` until loaded.
// Loaded once on auth bootstrap via useNotificationPreferences (called from RequireAuth).
export const notificationPreferencesAtom = atom<NotificationPreferences | null>(
  null
);

// Navigation
export const sidebarCollapsedAtom = atomWithStorage<boolean>(
  'sidebarCollapsed',
  false
);

// Starter Checklist
export const isStarterChecklistOpenAtom = atomWithStorage<boolean>(
  'isStarterChecklistOpen',
  true
);

// Warehouse Search Map Viewport
// Persisted so the viewport survives a return from a warehouse detail page — the
// nav bar and breadcrumbs are plain anchors, so that return is a full page reload.
export const warehouseSearchMapViewAtom = atomWithStorage<{
  center: { lat: number; lon: number };
  zoom: number;
} | null>('warehouseSearchMapView', null);

// Copilot — Imported Network Ratecard
// `id` changes on every fresh import so AddNetworkRatecard.tsx can use it as a React key to
// force NetworkRatecardEditor to remount with the new initial values when the user is already
// on `/network-ratecard/add` (React Router same-path navigate is a no-op, so the editor's
// `useState(initialZones)` would otherwise stay stuck on the first render's props).
//
// `extractionWarnings` carries plain-language messages from flox-mcp about anything dropped or
// unparseable in the source document — surfaced to the user as a yellow banner above the editor.
//
// `originalPostcodesInput` per zone preserves the source-format token list (e.g. "AL, CB1-5,
// PE38+") before `expandPostcodeInput` flattens it into discrete prefixes. The editor renders it
// as a "Imported as: …" subtitle so the user can recognise their source ratecard at a glance.
export type ImportedNetworkRatecard = {
  id: string;
  networkName: string;
  countryCode: string;
  // ISO 4217 currency code that applies to every price on the imported ratecard (zone services
  // and surcharges). Optional/undefined when the source document didn't state a currency — the
  // editor falls back to "GBP" since the platform is UK-only today.
  currency?: string;
  extractionWarnings?: string[];
  zones: Array<{
    zoneName: string;
    countryCode: string;
    postcodes: string[];
    originalPostcodesInput?: string;
    services: Array<{
      serviceName: string;
      price: number;
      // Pallet-tier ratecards carry these alongside the price; generic services (Standard /
      // Express) won't have them. Length in cm, weight in kg. AddNetworkRatecard pipes them
      // into the editor's `servicePricing[].palletType` / `palletDimensions` fields so the
      // user doesn't retype dimensions that are already in the source document.
      palletType?: string;
      palletDimensions?: {
        height?: number;
        width?: number;
        depth?: number;
        weight?: number;
      };
    }>;
  }>;
  // Network-wide surcharges from the carrier's "Additional Surcharges" / "Surcharges per
  // consignment" section. Empty/undefined when the source document had no surcharge block.
  // `unit` is free-form ("per consignment", "per pallet", "%", etc.) to preserve the user's
  // source vocabulary — the editor renders it verbatim.
  surcharges?: Array<{
    name: string;
    price: number;
    unit?: string;
    notes?: string;
  }>;
};
export const importedNetworkRatecardAtom = atom<ImportedNetworkRatecard | null>(null);

// True while the NetworkRatecardEditor has unsaved user edits. The Copilot reads this before
// firing a second import so it can prompt the user instead of silently nuking their work via the
// remount-key (`importedNetworkRatecardAtom.id`). Resets on successful save and on editor unmount.
export const networkRatecardEditorDirtyAtom = atom<boolean>(false);
